import * as React from "react";
import { connect } from "react-redux";
import { bindActionCreators, Dispatch } from "redux";
import * as _ from "lodash";
import { EditActions, editActions } from "../../actions/editActions";
import { nameofFactory, hasWriteAccess } from "../../utils/utils";
import { getDomainGroupNames, getBaseEnumOptions } from "../../utils/pdApiUtils";
import Editor from "../../components/Editing/Editor";
import NumberField from "../../components/Editing/NumberField";
import TextField from "../../components/Editing/TextField";
import DropdownField from "../../components/Editing/DropdownField";
import MultipleSelection from "../../components/Editing/MultipleSelection";


type StateProps = {
  editedDomain: Api.Domain;
  storedDomain: Api.Domain;
  pdApi: State.PdApi;
  activeUser?: Api.User;
};

type DispatchProps = {
  editActions: typeof editActions;
};

type OwnProps = {
  toggleModal: () => void;
};

const domainFields = nameofFactory<Api.Domain>();

export type EditDomainProps = StateProps &
  DispatchProps &
  OwnProps & {
    pdApi: State.PdApi;
  };

export class EditDomain extends React.Component<EditDomainProps> {
  render(): JSX.Element {
    const { editedDomain, pdApi, storedDomain, activeUser, toggleModal } = this.props;
    const domainOptions = getDomainGroupNames(pdApi);
    const electrificationOptions = getBaseEnumOptions(pdApi.electrifications);
    const isNew = !storedDomain;
    let filteredDomainOptions;

    if (activeUser && activeUser.roles.some(role => role.name === "admin")) {
      filteredDomainOptions = domainOptions;
    } else {
      filteredDomainOptions = domainOptions.filter(domainOption => {
        const groupName: Api.GroupName = domainOption.text as Api.GroupName;
        return hasWriteAccess(activeUser, groupName);
      });
      if (editedDomain.domainGroup.id === 1 && editedDomain.domainGroup.url === "" && isNew) {
        editedDomain.domainGroup.id = filteredDomainOptions[0].value;
        const domainGroup = pdApi.domainGroups[editedDomain.domainGroup.id];
        this.props.editActions.setEdited({ ...editedDomain, domainGroup });
      }
    }

    if (!isNew) {
      filteredDomainOptions = domainOptions;
    }

    const groupName: Api.GroupName | undefined = _.values(pdApi.domainGroups).find(
      (domainGroup: Api.DomainGroup) => domainGroup.id === editedDomain.domainGroup.id
    )?.enumName as Api.GroupName;

    const userPermissions = hasWriteAccess(
      activeUser,
      groupName === "Lift" ? ["Lift", "LIO"] : groupName
    );

    const parameterTargets: { [key: string]: string } = {
      Lift: "liftParameters",
      APM: "apmParameters",
      Drive: "driveParameters",
      Escalator: "escalatorParameters",
      Door: "doorParameters",
      Group: "groupParameters",
      Safety: "safetyParameters",
      Monitoring: "liftParameters",
      NonKone: "liftParameters",
      Unknown: "liftParameters",
      Signalization: "liftParameters",
      Network: "liftParameters",
    };

    const faultTargets: { [key: string]: string } = {
      Lift: "faults",
      APM: "apmFaults",
      Drive: "faults",
      Escalator: "escalatorFaults",
      Door: "doorFaults",
      Group: "faults",
      Safety: "faults",
      Monitoring: "monitoringFaults",
      NonKone: "nonKoneFaults",
      Unknown: "faults",
      Signalization: "signalizationFaults",
      Network: "networkFaults",
    };

    const versionTargets: { [key: string]: string } = {
      Door: "DoorVersions",
      Escalator: "EscalatorVersions",
      Drive: "driveVersions"
    };

    let domainAccess = false;

    if (this.props.editedDomain.extraDomainGroupName) {
      const parameterkey: string = parameterTargets[this.props.editedDomain.extraDomainGroupName];
      const faultKey: string = faultTargets[this.props.editedDomain.extraDomainGroupName];
      const versionKey: string = versionTargets[this.props.editedDomain.extraDomainGroupName];

      domainAccess =
        // eslint-disable-next-line
        (this.props.pdApi[parameterkey as keyof State.PdApi] as any[]).findIndex(
          parameter => parameter.domain.id === this.props.editedDomain.id
        ) > -1 ||
        // eslint-disable-next-line
        (this.props.pdApi[faultKey as keyof State.PdApi] as any[]).findIndex(
          fault => fault.domain.id === this.props.editedDomain.id
        ) > -1 ||
        (groupName === "Group" &&
          // eslint-disable-next-line
          Object.entries((this.props.pdApi["features"] as any[]) ?? {}).findIndex(
            ([, feature]) => feature.domain.id === this.props.editedDomain.id
          ) > -1) ||
        (!!versionKey &&
          // eslint-disable-next-line
          _.values(this.props.pdApi[versionKey as keyof State.PdApi] as any[]).findIndex(
            version => version.domain.id === this.props.editedDomain.id
          ) > -1) ||
        _.values(this.props.pdApi.parameterCategories ?? []).findIndex(
          domain => domain.domain?.id === this.props.editedDomain.id
        ) > -1;
    }

    return (
      <Editor
        itemName={"domain"}
        originalObject={storedDomain}
        editedObject={editedDomain}
        hasWritePermission={hasWriteAccess(
          activeUser,
          groupName === "Lift" ? ["Lift", "LIO"] : groupName
        )}
        isValid={this.isAllFieldsValid()}
        cancel={this.props.editActions.cancelEditing}
        update={() => this.props.editActions.update("domains")}
        create={() => this.props.editActions.add("domains")}
        copy={() => this.props.editActions.startEditing(editedDomain, true)}
        remove={{
          callback: toggleModal,
          disabled: domainAccess,
          disabledTooltip: "as Domain has child elements. Please contact Admin for further details."
        }}
      >
        <NumberField
          id="code"
          label="Code"
          value={editedDomain.enumValue}
          isError={!this.isFieldValid(domainFields("enumValue"))}
          errorMessage="The code must be unique."
          onChange={enumValue => this.props.editActions.setEdited({ ...editedDomain, enumValue })}
          readonly={!isNew || !userPermissions}
        />
        <TextField
          id="enumeration"
          label="Enumeration"
          value={editedDomain.enumName}
          isError={!this.isFieldValid(domainFields("enumName"))}
          errorMessage={
            !this.isEmptyString(editedDomain.enumName)
              ? "The enumeration must be unique and cannot begin with space."
              : ""
          }
          readonly={!isNew || !userPermissions}
          onChange={enumName => this.props.editActions.setEdited({ ...editedDomain, enumName })}
        />
        <TextField
          id="comment"
          label="Comment"
          value={editedDomain.swComment}
          onChange={swComment => this.props.editActions.setEdited({ ...editedDomain, swComment })}
          readonly={!userPermissions}
        />
        <DropdownField
          id="domainGroup"
          label="Domain Group"
          value={editedDomain.domainGroup.id}
          options={filteredDomainOptions}
          onChange={domainGroupIndex => {
            const domainGroup = {
              id: pdApi.domainGroups[domainGroupIndex].id,
              url: pdApi.domainGroups[domainGroupIndex].url
            };
            this.props.editActions.setEdited({ ...editedDomain, domainGroup });
          }}
          disabled={
            (
            (filteredDomainOptions.length === 1) //when only one option available
            || (activeUser && activeUser.roles.length === 1) //User has single role
            || (activeUser && !isNew && this.isDomaingroupAccessible()) //existing domain and no write access for domain group
            || (activeUser && !isNew && domainAccess) //disabled for EVERYONE (including admin) if has child elements
            || (activeUser && !isNew && !domainAccess && storedDomain.domainGroup.id !== 1 
              && !activeUser.roles.some(role => role.name === "admin"))
            ) //Non-admin can only change FROM Unknown domain (id=1)
          }//Admin users can change Domain Group if no child elements available
          //Target users can only swap domain group from unknown to their own domain group if no child elements available
          />
        <br />
        <MultipleSelection
          id="electrifications"
          label="Electrifications"
          value={
            editedDomain.electrifications &&
            editedDomain.electrifications.map(electrification => electrification.enumValue)
          }
          options={electrificationOptions}
          onChange={electrificationEnumValues => {
            const electrifications = electrificationEnumValues.map((enumValue: number) => ({
              enumValue
            }));
            this.props.editActions.setEdited({
              ...editedDomain,
              electrifications
            });
          }}
          disabled={!userPermissions}
        />
        {!userPermissions && <p className="color-red">Your role doesn't have neccessary rights</p>}
      </Editor>
    );
  }

  private isDomaingroupAccessible = (): boolean => {
    // eslint-disable-next-line
    const { storedDomain, activeUser, pdApi } = this.props;
    // eslint-disable-next-line
    const isNew = !storedDomain;
    if(!isNew) {
      const domainGroupName: Api.GroupName | undefined = _.values(pdApi.domainGroups).find(
        (domainGroup: Api.DomainGroup) => domainGroup.id === storedDomain.domainGroup.id
      )?.enumName as Api.GroupName;

      const hasAccess = hasWriteAccess(
        activeUser,
        domainGroupName === "Lift" ? ["Lift", "LIO"] : domainGroupName
      );

      return (!isNew && !hasAccess); //existing domain and no write access return true
    }
    else {
      return false;
    }
  };

  private isEmptyString = (str: string): boolean => !(!!str.trim() && str.trim().length > 0);

  private isFieldValid = (field: string): boolean => {
    const { pdApi, editedDomain } = this.props;
    switch (field) {
      case domainFields("enumValue"): {
        if (!Number.isInteger(editedDomain.enumValue)) return false;
        return _.values(pdApi.domains).find(
          domain => domain.id !== editedDomain.id && domain.enumValue === editedDomain.enumValue
        )
          ? false
          : true;
      }
      case domainFields("enumName"): {
        const { enumName } = editedDomain;
        if (this.isEmptyString(enumName) || editedDomain.enumName.startsWith(" ")) {
          return false;
        } else {
          return _.values(pdApi.domains).find(
            domain =>
              domain.id !== editedDomain.id && domain.enumName.toLowerCase().trim() === editedDomain.enumName.toLowerCase().trim()
          )
            ? false
            : true;
        }
      }
      default:
        return true;
    }
  };

  private isAllFieldsValid = (): boolean => {
    const { editedDomain } = this.props;
    const temp = _.keys(editedDomain);
    for (const key of temp) {
      if (!this.isFieldValid(key)) {
        return false;
      }
    }
    return true;
  };
}

const mapStateToProps = (state: State.Root) => ({
  editedDomain: state.editView.editedObject,
  storedDomain: state.editView.storedObject,
  pdApi: state.pdApi,
  activeUser: state.main.activeUser
});

const mapDispatchToProps = (dispatch: Dispatch<EditActions>) => ({
  editActions: bindActionCreators({ ...editActions }, dispatch)
});

export default connect(mapStateToProps, mapDispatchToProps)(EditDomain);
